export interface ChartSeries<T> {
  label: string
  /** A CSS colour, e.g. `var(--accent)`. */
  color: string
  value: (point: T) => number
}

/**
 * Bars per point, side by side or stacked, drawn with plain CSS so they scale
 * with the card. Hovering a bar shows every series for that point.
 */
export function BarChart<T>({
  data,
  series,
  label,
  format,
  stacked = false,
  height = 150,
}: {
  data: T[]
  series: ChartSeries<T>[]
  label: (point: T) => string
  format: (value: number) => string
  stacked?: boolean
  height?: number
}) {
  const totals = data.map((point) => (stacked ? series.reduce((sum, s) => sum + Math.max(0, s.value(point)), 0) : Math.max(0, ...series.map((s) => s.value(point)))))
  const max = Math.max(0, ...totals)
  const percent = (value: number) => (max > 0 ? `${(Math.max(0, value) / max) * 100}%` : '0%')
  const [first, last] = [data[0], data.at(-1)]
  return (
    <div className="chart">
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.label}>
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        <span className="muted">Peak {format(max)}</span>
      </div>
      <div className="chart-plot" style={{ height }} role="img" aria-label={series.map((s) => s.label).join(', ')}>
        {data.map((point, index) => (
          <div key={index} className={`chart-slot${stacked ? ' stacked' : ''}`} title={[label(point), ...series.map((s) => `${s.label}: ${format(s.value(point))}`)].join('\n')}>
            {series.map((s) => (
              <div key={s.label} className="chart-bar" style={{ height: percent(s.value(point)), background: s.color }} />
            ))}
          </div>
        ))}
      </div>
      {first !== undefined && last !== undefined && (
        <div className="chart-axis">
          <span>{label(first)}</span>
          <span>{label(last)}</span>
        </div>
      )}
    </div>
  )
}
